import {MAX_SEQUENCES_PER_ANNOTATION_REQUEST} from 'antibodies/domain-annotation/DomainAnnotationConstants';
import {annotateDomainSequences} from 'antibodies/domain-annotation/DomainAnnotationDAO';
import {SequenceAnnotationConfig} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import {pluralize} from 'BenchlingUtil';
import FileStates from 'FileStates';
import notifications, {Response} from 'notifications';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';

const ANNOTATION_FAILED_MESSAGE =
  'An error occurred while annotating the domain sequences. Please try again.';

function getAnnotatedHeading({
  sequenceType,
  annotatedCount,
  totalCount,
  batchDescription,
}: {
  sequenceType: string;
  annotatedCount: number;
  totalCount: number;
  batchDescription: string | null;
}): string {
  if (batchDescription != null) {
    return `Annotated ${batchDescription} ${sequenceType} domain sequences`;
  }
  const noun = `${sequenceType} domain ${pluralize(totalCount, 'sequence')}`;
  return annotatedCount === totalCount
    ? `Annotated ${totalCount} ${noun}`
    : `Annotated ${annotatedCount} of ${totalCount} ${noun}`;
}

/**
 * @param batchDescription Which of all the sequences being annotated this batch covers, such as "1–500 of
 * 1200", or null when all of them fit in one batch.
 */
function startAnnotationBatch({
  sequenceIds,
  config,
  batchDescription,
}: {
  sequenceIds: Array<string>;
  config: SequenceAnnotationConfig;
  batchDescription: string | null;
}): void {
  const taskId = TrayTaskActions.createTrayTask({
    heading:
      batchDescription != null
        ? `Annotating ${batchDescription} ${config.sequenceType} domain sequences`
        : `Annotating ${sequenceIds.length} ${config.sequenceType} domain ${pluralize(sequenceIds.length, 'sequence')}`,
  });
  TrayTaskActions.onProgressFor(taskId)(TrayTaskConstants.INDETERMINATE_PROGRESS);

  annotateDomainSequences({sequenceIds, config}).then(
    ({datasetId, results}) => {
      const failedCount = results.filter(({error}) => error != null).length;
      const openDataset = (): void => {
        FileStates.get().openFolderItem(datasetId);
      };
      TrayTaskActions.updateTrayTask(taskId, {
        progress: TrayTaskConstants.SUCCESS_PROGRESS,
        heading: getAnnotatedHeading({
          sequenceType: config.sequenceType,
          annotatedCount: results.length - failedCount,
          totalCount: results.length,
          batchDescription,
        }),
        subheading:
          failedCount > 0
            ? `${failedCount} ${pluralize(failedCount, 'sequence')} could not be annotated. Open the results dataset for details.`
            : undefined,
        onHeadingClick: openDataset,
        onSubheadingClick: openDataset,
      });
    },
    (res: Response) => {
      TrayTaskActions.updateTrayTask(taskId, {
        progress: TrayTaskConstants.FAILURE_PROGRESS,
        error: notifications.getFriendlyError(res) ?? ANNOTATION_FAILED_MESSAGE,
      });
    }
  );
}

/**
 * Starts annotating existing domain sequences and tracks the annotation in the tray, linking to the results
 * dataset when it finishes. Never throws: failures are reported on the tray task.
 *
 * Sequences beyond the per-request limit are split into batches, each with its own tray task and results
 * dataset. The tray headings of a batch name the range of sequences it covers, and its subheading reports the
 * sequences in it that could not be annotated.
 */
export default function startDomainSequenceAnnotation({
  sequenceIds,
  config,
}: {
  sequenceIds: Array<string>;
  config: SequenceAnnotationConfig;
}): void {
  const batchSize = MAX_SEQUENCES_PER_ANNOTATION_REQUEST;
  const isBatched = sequenceIds.length > batchSize;
  for (let start = 0; start < sequenceIds.length; start += batchSize) {
    const batch = sequenceIds.slice(start, start + batchSize);
    startAnnotationBatch({
      sequenceIds: batch,
      config,
      batchDescription: isBatched ? `${start + 1}–${start + batch.length} of ${sequenceIds.length}` : null,
    });
  }
}
